
import Vue from 'vue';
import {
  AppointmentModel,
  ClassroomModel,
  CourseModel,
  ListModel,
  UserModel,
} from '@descomplica/dex-models';
import { TranslateResult } from 'vue-i18n';
import { isMobileDevice } from '@/helpers/mobile';
import CalendarCard from '../components/cards/CalendarCard.vue';
import CardSpecial from '../components/cards/CardSpecial.vue';
import ListCard from '../components/cards/ListCard.vue';
import Alert from '../components/Alert.vue';
import i18n from '../i18n';
import { MicrocertificateModel } from '@/models/microcertificates/microcertificate.model';
import { Icon } from '@descomplica/vue-components';
import CalendarCardSkeleton from '../components/skeletons/CalendarCardSkeleton.vue';
import { Swiper, SwiperSlide } from 'vue-awesome-swiper';
import SwiperCore, { Navigation, Pagination } from 'swiper/core';
import PostGradAssessmentsCard from '@/components/cards/assessments/PostGradAssessmentsCard.vue';
import { capitalizeAllWords } from '@/helpers/string';
import Analytics from '@/helpers/analytics';
import { EVENT_LABELS } from '@/helpers/analytics/labels';
import { ANALYTICS_LABELS } from '@/helpers/listItems';
import { getCourseCategory } from '@/helpers/category';
import { getEventLiveType } from '@/helpers/analytics/eventProperties';
import NotesCard from '../components/cards/NotesCard.vue';
import { NoteModel } from '@/models/student-notes/note.model';
import { isSourceTypeValid } from '@/helpers/lives';
import GraduationDateCard from '@/components/cards/postgrad/GraduationDateCard.vue';
import { ChimeraCourseModel } from '@/models/chimera/ChimeraCourseModel';

SwiperCore.use([Pagination, Navigation]);

interface ListWithClassroomSlug {
  list: ListModel | {};
  classroomSlug: string;
}

interface Data {
  swiperOption: Object;
}

export default Vue.extend({
  components: {
    Alert,
    ListCard,
    CalendarCard,
    CardSpecial,
    Swiper,
    SwiperSlide,
    Icon,
    CalendarCardSkeleton,
    PostGradAssessmentsCard,
    NotesCard,
    GraduationDateCard,
  },
  data(): Data {
    return {
      swiperOption: {
        spaceBetween: 30,
        pagination: {
          el: '.swiper-pagination',
          clickable: true,
        },
        navigation: {
          nextEl: '.swiper-button-next',
          prevEl: '.swiper-button-prev',
        },
        breakpoints: {
          1024: {
            slidesPerView: 4,
            spaceBetween: 30,
          },
          980: {
            slidesPerView: 4,
            spaceBetween: 100,
          },
          940: {
            slidesPerView: 4,
            spaceBetween: 120,
          },
          900: {
            slidesPerView: 4,
            spaceBetween: 200,
          },
          860: {
            slidesPerView: 4,
            spaceBetween: 230,
          },
          700: {
            slidesPerView: 4,
            spaceBetween: 240,
          },
          640: {
            slidesPerView: 4,
            spaceBetween: 230,
          },
          600: {
            slidesPerView: 2,
            spaceBetween: 0,
          },
          500: {
            slidesPerView: 1,
            spaceBetween: -170,
          },
          433: {
            slidesPerView: 1,
            spaceBetween: -100,
          },
          400: {
            slidesPerView: 1,
            spaceBetween: -70,
          },
          355: {
            slidesPerView: 1,
            spaceBetween: -30,
          },
          330: {
            slidesPerView: 1,
            spaceBetween: -10,
          },
          200: {
            slidesPerView: 1,
            spaceBetween: 5,
          },
        },
      },
    };
  },
  computed: {
    isLoadingUserBaseProgress(): boolean {
      return this.$store.getters.isLoadingUserBaseProgress;
    },
    isLoadingNotes(): boolean {
      return this.$store.getters.isLoadingNotes;
    },
    user(): UserModel {
      return this.$store.getters.currentUser;
    },
    currentCourse(): CourseModel {
      return this.$store.getters.currentCourse;
    },
    isPostGradCourse(): boolean {
      return this.currentCourse.descomplicaCategorySlug === 'pos';
    },
    isPreparingDownloadMicroCertificate(): boolean {
      return this.$store.getters.isGeneratingMicroCertificate;
    },
    lastNotes(): NoteModel[] {
      return this.$store.getters.getHomeNotes;
    },
    appointments(): AppointmentModel[] {
      const appointments: AppointmentModel[] =
        this.$store.getters.appointmentsByUserClassroomsIdsToHappen || [];

      return appointments.slice(0, 8);
    },
    specials(): any {
      return [];
    },
    listsTitle(): TranslateResult {
      if (this.stoppedLists.length) {
        return i18n.t('keepStuding');
      }

      if (this.associatedLists.length) {
        return i18n.t('startStuding');
      }

      return '';
    },
    lists(): ListWithClassroomSlug[] {
      if (this.stoppedLists.length) {
        return this.stoppedLists;
      }

      if (this.associatedLists.length) {
        return this.associatedLists;
      }

      const emptyList = {
        classroomSlug: '',
        list: {},
      };

      // empty data for skeleton state
      return [emptyList, emptyList, emptyList];
    },
    stoppedLists(): ListWithClassroomSlug[] {
      return this.user
        .getClassroomsByCourseId(this.currentCourse.id, true)
        .reduce((acc: ListWithClassroomSlug[], classroom) => {
          classroom.getStoppedLists().forEach((list: ListModel) => {
            acc.push({
              list,
              classroomSlug: classroom.slug,
            });
          });
          return acc;
        }, [])
        .slice(0, 3);
    },
    associatedLists(): ListWithClassroomSlug[] {
      return this.user
        .getClassroomsByCourseId(this.currentCourse.id, true)
        .reduce((acc: ListWithClassroomSlug[], classroom: ClassroomModel) => {
          classroom.getAssociatedLists().forEach((list: ListModel) => {
            acc.push({
              list,
              classroomSlug: classroom.slug,
            });
          });
          return acc;
        }, [])
        .slice(0, 3);
    },
    alert(): Object {
      return {
        type: 'paymentSlip',
        target: '_blank',
        src: '//descomplica.lyceum.com.br/AOnline/AOnline/financeiro/T215D.tp',
        text: `Olá, ${this.user.firstName}! Você possui um boleto em aberto com vencimento no dia 15/10/2019. Não esqueça de realizar o pagamento e não gerar multa.`,
      };
    },
    hasAppointments(): Boolean {
      return this.appointments && this.appointments.length > 0;
    },
    hasSpecials(): Boolean {
      return this.specials && this.specials.length > 0;
    },
    microcertificates(): MicrocertificateModel[] {
      return this.$store.getters.userMicrocertificates;
    },
    showPostgradAssessmentsCard(): boolean {
      return this.$store.getters.showPostgradAssessmentsCard;
    },
    canViewPostgradStudyPlanning(): boolean {
      return this.$store.getters.canViewPostgradStudyPlanning;
    },
    currentChimeraCourse(): ChimeraCourseModel | null {
      return this.$store.getters.getCurrentChimeraCourse;
    },
  },
  watch: {
    canViewPostgradStudyPlanning: {
      handler(value: boolean) {
        if (value) {
          this.$router.push({
            name: 'postgradStudyPlanning',
            params: {
              category: this.currentCourse.descomplicaCategorySlug as string,
              currentCourse: this.currentCourse.slug as string,
            },
          });
        }
      },
      immediate: true,
    },
  },
  mounted() {
    if (this.currentCourse && this.currentCourse.name) {
      document.title = `Descomplica | ${capitalizeAllWords(this.currentCourse.name)}`;
    }

    if (this.isPostGradCourse)
      this.$store.dispatch('GET_POSTGRAD_CONTRACT_EXTENSION_ELIGIBILITY', {
        course: this.currentCourse,
        chimeraCourse: this.currentChimeraCourse,
      });

    this.pullUserNotes();
    this.initializeSmartBanner();
  },
  methods: {
    trackingContentOpened(appointment: AppointmentModel): void {
      const { classroom, event } = appointment;

      Analytics.trackEvent(EVENT_LABELS.contentOpened, {
        email: this.user.email,
        course_name: this.currentCourse.name,
        discipline_name: classroom.name,
        live_name: event?.name,
        content_type: ANALYTICS_LABELS.tutoring,
        source_type: event?.sourceType,
        category: getCourseCategory(this.currentCourse),
        live_type: getEventLiveType(event),
      });
    },
    goToLive(appointment: AppointmentModel): void {
      if (!appointment.event || !isSourceTypeValid(appointment.event.sourceType)) {
        return;
      }

      const { classroom, event } = appointment;
      const classroomSlug: string = classroom.slug;

      this.trackingContentOpened(appointment);

      if (event.sourceType === 'Zoom' && isMobileDevice()) {
        window.open(event.url);
        return;
      }

      this.$router.push({
        name: 'live',
        params: {
          classroomSlug,
          liveId: event.id.toString(),
        },
      });
    },
    trackSmartBannerViewed(): void {
      Analytics.trackEvent(EVENT_LABELS.smartBannerViewed, {
        application: 'Faculdade Descomplica',
      });
    },
    trackSmartBannerClose(): void {
      Analytics.trackEvent(EVENT_LABELS.smartBannerClicked, {
        button: 'close',
        application: 'Faculdade Descomplica',
      });
    },
    trackSmartBannerClick(): void {
      Analytics.trackEvent(EVENT_LABELS.smartBannerClicked, {
        button: 'download',
        application: 'Faculdade Descomplica',
      });
    },
    initializeSmartBanner() {
      document.addEventListener('smartbanner.view', this.trackSmartBannerViewed);
      document.addEventListener('smartbanner.clickout', this.trackSmartBannerClick);
      document.addEventListener('smartbanner.exit', this.trackSmartBannerClose);

      window.smartbanner?.publish();
    },
    async pullUserNotes() {
      await this.$store.dispatch('GET_LAST_VIDEO_NOTES');
    },
  },
});
